import React, { useState } from 'react';
import { API_BASE_URL } from '../config/api';

interface BulkUploadJob {
  id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  totalQueries: number;
  processedQueries: number;
  currentQuery?: string;
  error?: string;
}

export function BulkUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'processing' | 'complete' | 'error'>('idle');
  const [job, setJob] = useState<BulkUploadJob | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'text/csv') {
      setFile(droppedFile);
      setError(null);
    } else {
      setError('Please upload a CSV file');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Please select a CSV file');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploadStatus('uploading');
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_BASE_URL}/bulk-upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      setJob(data.job);
      setUploadStatus('processing');

      // Start polling for job status
      pollJobStatus(data.job.id);
    } catch (err: any) {
      setError(err.message || 'Failed to upload file');
      setUploadStatus('error');
    }
  };

  const pollJobStatus = async (jobId: string) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/bulk-upload/${jobId}`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch job status');
        }

        const data = await response.json();
        setJob(data.job);

        if (data.job.status === 'completed') {
          setUploadStatus('complete');
          clearInterval(interval);
        } else if (data.job.status === 'failed') {
          setUploadStatus('error');
          setError(data.job.error || 'Job failed');
          clearInterval(interval);
        }
      } catch (err: any) {
        setError(err.message);
        setUploadStatus('error');
        clearInterval(interval);
      }
    }, 2000); // Poll every 2 seconds
  };

  const resetUpload = () => {
    setFile(null);
    setUploadStatus('idle');
    setJob(null);
    setError(null);
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-100 mb-2">Bulk Search Upload</h2>
          <p className="text-slate-400 text-sm">
            Upload a CSV file to automatically run multiple searches. Pro feature only.
          </p>
        </div>
        <div className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm font-medium">
          PRO
        </div>
      </div>

      {/* CSV Format Info */}
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-semibold text-slate-200 mb-2">CSV Format Required:</h3>
        <div className="text-xs text-slate-400 space-y-1">
          <p><strong>Column 1:</strong> List Name (e.g., "Denver Coffee Shops")</p>
          <p><strong>Column 2:</strong> Search Query (e.g., "coffee shops in Denver, CO")</p>
          <p><strong>Column 3:</strong> Search Depth - Options: default, more, maximum</p>
        </div>
        <div className="mt-3 text-xs text-cyan-400">
          Example: <code className="bg-slate-800 px-2 py-1 rounded">Denver Coffee,coffee shops in Denver CO,default</code>
        </div>
      </div>

      {/* Upload Area */}
      {uploadStatus === 'idle' && (
        <>
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? 'border-cyan-500 bg-cyan-500/10'
                : 'border-slate-600 hover:border-slate-500'
            }`}
          >
            {file ? (
              <div className="space-y-4">
                <svg className="mx-auto h-12 w-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-slate-200">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-slate-400">{(file.size / 1024).toFixed(2)} KB</p>
                </div>
                <button
                  onClick={() => setFile(null)}
                  className="text-sm text-cyan-400 hover:text-cyan-300"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <div className="text-slate-300">
                  <label htmlFor="file-upload" className="cursor-pointer text-cyan-400 hover:text-cyan-300">
                    Upload a CSV file
                  </label>
                  <span className="text-slate-400"> or drag and drop</span>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </div>
            )}
          </div>

          {file && (
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleUpload}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg font-medium transition-all"
              >
                Start Bulk Upload
              </button>
              <button
                onClick={resetUpload}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </>
      )}

      {/* Processing Status */}
      {(uploadStatus === 'uploading' || uploadStatus === 'processing') && job && (
        <div className="space-y-4">
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-100">
                {uploadStatus === 'uploading' ? 'Uploading...' : 'Processing Searches'}
              </h3>
              <span className="text-sm text-slate-400">
                {job.processedQueries} / {job.totalQueries}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-slate-700 rounded-full h-2 mb-4">
              <div
                className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(job.processedQueries / job.totalQueries) * 100}%` }}
              ></div>
            </div>

            {job.currentQuery && (
              <div className="text-sm text-slate-400">
                Current: <span className="text-cyan-400">{job.currentQuery}</span>
              </div>
            )}

            <div className="mt-4 flex items-center gap-2 text-sm text-slate-400">
              <svg className="animate-spin h-4 w-4 text-cyan-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing... This may take several minutes
            </div>
          </div>
        </div>
      )}

      {/* Complete */}
      {uploadStatus === 'complete' && job && (
        <div className="space-y-4">
          <div className="bg-green-900/20 border border-green-600 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <svg className="h-8 w-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-lg font-semibold text-green-400">Bulk Upload Complete!</h3>
                <p className="text-sm text-green-300">
                  Successfully processed {job.totalQueries} searches
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              All searches have been enriched and saved to your account. You can find them in the Saved Searches sidebar.
            </p>
            <button
              onClick={resetUpload}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
            >
              Upload Another File
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-900/50 border border-red-600 rounded-lg p-4 text-red-200">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
          {uploadStatus === 'error' && (
            <button
              onClick={resetUpload}
              className="mt-3 text-sm text-red-300 hover:text-red-200 underline"
            >
              Try again
            </button>
          )}
        </div>
      )}
    </div>
  );
}
